<script setup lang="ts">
import { saveAs } from 'file-saver'
import VChart from 'vue-echarts'
import { MapboxMap, MapboxLayer, MapboxMarker } from '@studiometa/vue-mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'
import type { ECOption } from '@/plugins/echarts'
import { notifyError } from '@/utils/helpers'



const theme = useTheme()
const utilisateurStore = useUtilisateurStore()
const site = ref(utilisateurStore.siteSélectionné)
const isLoading = ref(false)
const startDate = ref('')
const endDate = ref('')
const latitude = ref(site.value.latitudeGoogleMaps)
const longitude = ref(site.value.longitudeGoogleMaps)
const markerPos = computed(() => [longitude.value, latitude.value] as [number, number])
const mapInitialCenter = [longitude.value, latitude.value] as [number, number]
const accessToken = 'pk.eyJ1IjoiYWdyaW1ldHJpZSIsImEiOiJjbTc3cjhrMTgxZGNqMnFxOGM4aGpjaW5hIn0.fUMzok-3aNnM9Zg7hpBKjQ'
const mapRef = ref<InstanceType<typeof MapboxMap> | null>(null)

// ── Field polygons ────────────────────────────────────────────────────────
const showChamps = ref(true)
const selectedProducers = ref<string[]>([])

const champsData = ref({ type: 'FeatureCollection', features: [] } as any)

const producerColors = [
  '#4CAF50', '#2196F3', '#FF9800', '#9C27B0', '#F44336',
  '#00BCD4', '#E91E63', '#8BC34A', '#3F51B5', '#FF5722',
  '#009688', '#673AB7', '#CDDC39', '#795548', '#607D8B',
  '#FFC107', '#03A9F4', '#76FF03', '#FF4081', '#536DFE',
]

const producerColorMap = computed(() => {
  const unique = [...new Set(
    champsData.value.features
      .map((f: any) => f.properties?.fast_carto)
      .filter((v: any) => v != null)
  )] as string[]
  const map = new Map<string, string>()
  unique.forEach((p, i) => map.set(p, producerColors[i % producerColors.length]))
  return map
})

const champsLabelsData = computed(() => {
  const features = champsData.value.features.map((feature: any) => {
    const coords = feature.geometry?.coordinates
    if (!coords?.length) return null
    const ring = feature.geometry.type === 'MultiPolygon' ? coords[0][0] : coords[0]
    if (!ring?.length) return null
    let sumX = 0, sumY = 0
    for (const p of ring) { sumX += p[0]; sumY += p[1] }
    const label = feature.properties?.IDENCHAMP || ''
    if (!label) return null
    return { type: 'Feature', geometry: { type: 'Point', coordinates: [sumX / ring.length, sumY / ring.length] }, properties: { IDENCHAMP: label } }
  }).filter(Boolean)
  return { type: 'FeatureCollection', features }
})

const champsGroupLabelsData = computed(() => {
  const groups = new Map<string, number[][]>()
  for (const feature of champsData.value.features) {
    const producer = feature.properties?.fast_carto
    if (!producer) continue
    const coords = feature.geometry?.coordinates
    if (!coords?.length) continue
    const ring = feature.geometry.type === 'MultiPolygon' ? coords[0][0] : coords[0]
    if (!ring?.length) continue
    let sumX = 0, sumY = 0
    for (const p of ring) { sumX += p[0]; sumY += p[1] }
    if (!groups.has(producer)) groups.set(producer, [])
    groups.get(producer)!.push([sumX / ring.length, sumY / ring.length])
  }
  const features = [...groups.entries()].map(([producer, centroids]) => ({
    type: 'Feature',
    geometry: { type: 'Point', coordinates: [
      centroids.reduce((s, c) => s + c[0], 0) / centroids.length,
      centroids.reduce((s, c) => s + c[1], 0) / centroids.length,
    ]},
    properties: { fast_carto: producer },
  }))
  return { type: 'FeatureCollection', features }
})

function buildFillColorExpression() {
  const entries = [...producerColorMap.value.entries()]
  if (entries.length === 0) return '#FFEB3B'
  const expr: any[] = ['match', ['get', 'fast_carto']]
  for (const [key, color] of entries) expr.push(key, color)
  expr.push('#FFEB3B')
  return expr
}

function buildOutlineColorExpression() {
  const entries = [...producerColorMap.value.entries()]
  if (entries.length === 0) return '#F57F17'
  const expr: any[] = ['match', ['get', 'fast_carto']]
  for (const [key, color] of entries) expr.push(key, color)
  expr.push('#F57F17')
  return expr
}

const champsLayerOptions = computed(() => ({
  type: 'fill',
  source: { type: 'geojson', data: champsData.value },
  layout: { visibility: showChamps.value ? 'visible' : 'none' },
  paint: { 'fill-color': buildFillColorExpression(), 'fill-opacity': 0.35, 'fill-outline-color': buildOutlineColorExpression() },
}))

const champsOutlineLayerOptions = computed(() => ({
  type: 'line',
  source: { type: 'geojson', data: champsData.value },
  layout: { visibility: showChamps.value ? 'visible' : 'none' },
  paint: { 'line-color': buildOutlineColorExpression(), 'line-width': 2 },
}))

const champsLabelLayerOptions = computed(() => ({
  type: 'symbol',
  source: { type: 'geojson', data: champsLabelsData.value },
  minzoom: 11,
  layout: {
    'text-field': ['get', 'IDENCHAMP'],
    'text-size': 12,
    'text-anchor': 'center',
    'text-allow-overlap': false,
    'text-ignore-placement': false,
    'text-optional': true,
    visibility: showChamps.value ? 'visible' : 'none',
  },
  paint: { 'text-color': '#000000', 'text-halo-color': '#FFFFFF', 'text-halo-width': 1.5 },
}))

const champsGroupLabelLayerOptions = computed(() => {
  const entries = [...producerColorMap.value.entries()]
  let textColor: any = '#FFFFFF'
  if (entries.length > 0) {
    textColor = ['match', ['get', 'fast_carto']]
    for (const [key, color] of entries) textColor.push(key, color)
    textColor.push('#FFFFFF')
  }
  return {
    type: 'symbol',
    source: { type: 'geojson', data: champsGroupLabelsData.value },
    layout: {
      'text-field': ['get', 'fast_carto'],
      'text-size': 15,
      'text-anchor': 'center',
      'text-allow-overlap': false,
      'text-optional': true,
      'text-font': ['DIN Offc Pro Bold', 'Arial Unicode MS Bold'],
      'text-letter-spacing': 0.05,
      visibility: showChamps.value ? 'visible' : 'none',
    },
    paint: { 'text-color': textColor, 'text-halo-color': 'rgba(0,0,0,0.75)', 'text-halo-width': 2 },
  }
})

const champsFilterExpression = computed(() => {
  const all = [...producerColorMap.value.keys()]
  if (all.length === 0 || selectedProducers.value.length === all.length) return null
  if (selectedProducers.value.length === 0) return ['boolean', false]
  return ['in', ['get', 'fast_carto'], ['literal', selectedProducers.value]]
})

watch(selectedProducers, () => {
  const map = mapRef.value?.map
  if (!map) return
  const filter = champsFilterExpression.value
  for (const id of ['champs-polygons', 'champs-outlines', 'champs-labels', 'champs-group-labels']) {
    if (map.getLayer(id)) map.setFilter(id, filter as any)
  }
}, { deep: true })

async function getGeoJson() {
  try {
    const siteCS = utilisateurStore.siteSélectionné.lettresDappel
    const result = await apiProphet.get(`prsm/${siteCS}/champs/geojson`)
    const jsons = result.data as any[]
    const allFeatures: any[] = []
    for (const geojson of jsons) {
      if (geojson.type === 'FeatureCollection' && geojson.features) allFeatures.push(...geojson.features)
      else if (geojson.type === 'Feature') allFeatures.push(geojson)
    }
    champsData.value = { type: 'FeatureCollection', features: allFeatures }
    selectedProducers.value = [...producerColorMap.value.keys()]

    const map = mapRef.value?.map
    if (map) {
      for (const [src, data] of [
        ['champs-polygons', champsData.value],
        ['champs-outlines', champsData.value],
        ['champs-labels',   champsLabelsData.value],
        ['champs-group-labels', champsGroupLabelsData.value],
      ] as [string, any][]) {
        if (map.getSource(src)) (map.getSource(src) as any).setData(data)
      }
      if (map.getLayer('champs-polygons')) {
        map.setPaintProperty('champs-polygons', 'fill-color', buildFillColorExpression())
        map.setPaintProperty('champs-polygons', 'fill-outline-color', buildOutlineColorExpression())
      }
      if (map.getLayer('champs-outlines'))
        map.setPaintProperty('champs-outlines', 'line-color', buildOutlineColorExpression())
    }
  } catch (error) {
    const status = (error as any)?.response?.status
    const requestUrl = String((error as any)?.config?.url ?? '')
    const isChampsJson404 =
      status === 404
      && /prsm\/[^/]+\/champs\/(json|geojson)/i.test(requestUrl)

    if (!isChampsJson404)
      notifyError(error)
  }
}

onMounted(() => { getGeoJson() })

function onMapClick(e: any) {
  latitude.value  = Math.round(e.lngLat.lat * 1e6) / 1e6
  longitude.value = Math.round(e.lngLat.lng * 1e6) / 1e6
}

const headers = [
  { title: 'Date', key: 'date' },
  { title: 'Température moy. (°C)', key: 'temperature' },
  { title: 'Température min (°C)', key: 'minTemperature' },
  { title: 'Température max (°C)', key: 'maxTemperature' },
  { title: 'Précipitations (mm)', key: 'rainfall' },
]

declare interface WeatherRow {
  date: string
  temperature: number
  minTemperature: number
  maxTemperature: number
  rainfall: number
}

const items = ref<WeatherRow[]>([])

async function loadHistoricalData() {
  isLoading.value = true
  items.value = []
  try {
    const config = {
      params: {
        latitude: latitude.value,
        longitude: longitude.value,
        startDate: startDate.value,
        endDate: endDate.value,
      },
    }
    const response = await apiOmni.get('/api/azuremaps/weather/historical/actuals/daily', config)

    if (response.data.results &&
        Array.isArray(response.data.results) &&
        response.data.results.length > 0) {
      items.value = response.data.results.map((result: any) => ({
        date: result.date,
        temperature: result.temperature.average.value,
        minTemperature: result.temperature.minimum.value,
        maxTemperature: result.temperature.maximum.value,
        rainfall: result.precipitation.value,
      }))
      updateChart()
    }
  } catch (error) {
    console.error('Error loading weather:', error)
  } finally {
    isLoading.value = false
  }
}

function updateChart() {
  chartOptions.value = {
    ...chartOptions.value,
    xAxis: {
      ...(chartOptions.value.xAxis as object),
      data: items.value.map(item => item.date),
    },
    series: [
      {
        name: 'Température moy.',
        type: 'line',
        data: items.value.map(item => item.temperature),
        yAxisIndex: 0,
        showSymbol: false,
        smooth: true,
        lineStyle: { width: 2 },
      },
      {
        name: 'Précipitations',
        type: 'bar',
        data: items.value.map(item => item.rainfall),
        yAxisIndex: 1,
        barMaxWidth: 14,
      },
    ],
  }
}

const chartOptions = ref<ECOption>({
  backgroundColor: 'transparent',
  tooltip: { trigger: 'axis' },
  legend: { data: ['Température moy.', 'Précipitations'] },
  grid: { top: 44, left: '2%', right: '3%', bottom: '3%', containLabel: true },
  xAxis: {
    type: 'category',
    boundaryGap: true,
    data: [],
  },
  yAxis: [
    { type: 'value', name: 'Température (°C)', position: 'left' },
    { type: 'value', name: 'Précipitations (mm)', position: 'right', min: 0 },
  ],
  series: [
    { name: 'Température moy.', type: 'line', data: [], yAxisIndex: 0, showSymbol: false, smooth: true, lineStyle: { width: 2 } },
    { name: 'Précipitations',   type: 'bar',  data: [], yAxisIndex: 1, barMaxWidth: 14 },
  ],
})

const exportCSV = () => {
  if (items.value.length === 0) return
  const csvContent = [
    'Date,Température moy. (°C),Température min (°C),Température max (°C),Précipitations (mm)',
    ...items.value.map(i =>
      `${i.date},${i.temperature},${i.minTemperature},${i.maxTemperature},${i.rainfall}`
    ),
  ].join('\n')
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  saveAs(blob, `données_météo_${startDate.value}_${endDate.value}.csv`)
}
</script>

<template>
  <v-card class="mx-4 my-4" rounded="xl" :loading="isLoading">

    <!-- Page header -->
    <div class="page-header px-5 pt-5 pb-4">
      <div class="d-flex flex-wrap" style="gap: 16px;">

        <!-- Left: title + controls -->
        <div class="d-flex flex-column" style="gap: 14px; flex: 1 1 280px; min-width: 0;">
          <div class="d-flex align-center" style="gap: 12px;">
            <v-avatar color="primary" variant="tonal" size="44" rounded="lg">
              <v-icon icon="M12.74,5.47C15.1,6.5 16.35,9.03 15.92,11.46C17.19,12.56 18,14.19 18,16V16.17C18.31,16.06 18.65,16 19,16A3,3 0 0,1 22,19A3,3 0 0,1 19,22H6A4,4 0 0,1 2,18A4,4 0 0,1 6,14H6.27C5,12.45 4.6,10.24 5.5,8.26C6.72,5.5 9.97,4.24 12.74,5.47M11.93,7.3C10.16,6.5 8.09,7.31 7.31,9.07C6.85,10.09 6.93,11.22 7.41,12.13C8.5,10.83 10.16,10 12,10C12.7,10 13.38,10.12 14,10.34C13.94,9.06 13.18,7.86 11.93,7.3M13.55,3.64C13,3.4 12.45,3.23 11.88,3.12L14.37,1.82L15.27,4.71C14.76,4.29 14.19,3.93 13.55,3.64M6.09,4.44C5.6,4.79 5.17,5.19 4.8,5.63L4.91,2.82L7.87,3.5C7.25,3.71 6.65,4.03 6.09,4.44M18,9.71C17.91,9.12 17.78,8.55 17.59,8L19.97,9.5L17.92,11.73C18.03,11.08 18.05,10.4 18,9.71M3.04,11.3C3.11,11.9 3.24,12.47 3.43,13L1.06,11.5L3.1,9.28C3,9.93 2.97,10.61 3.04,11.3M19,18H16V16A4,4 0 0,0 12,12A4,4 0 0,0 8,16H6A2,2 0 0,0 4,18A2,2 0 0,0 6,20H19A1,1 0 0,0 20,19A1,1 0 0,0 19,18Z" size="26"/>
            </v-avatar>
            <div>
              <div class="text-h6 font-weight-bold" style="line-height: 1.2;">Données météo historiques</div>
              <div class="text-caption text-medium-emphasis">Températures &amp; précipitations journalières</div>
            </div>
          </div>

          <!-- Date controls -->
          <div class="d-flex flex-wrap align-center" style="gap: 10px;">
            <v-text-field
              v-model="startDate"
              label="Date de début"
              type="date"
              variant="outlined"
              density="compact"
              rounded="lg"
              hide-details
              style="min-width: 160px; flex: 1 1 160px;"
            />
            <v-text-field
              v-model="endDate"
              label="Date de fin"
              type="date"
              variant="outlined"
              density="compact"
              rounded="lg"
              hide-details
              style="min-width: 160px; flex: 1 1 160px;"
            />
            <v-btn
              color="primary"
              variant="tonal"
              rounded="pill"
              :loading="isLoading"
              prepend-icon="M17,3A2,2 0 0,1 19,5V15A2,2 0 0,1 17,17H13V19H14A1,1 0 0,1 15,20H22V22H15A1,1 0 0,1 14,23H10A1,1 0 0,1 9,22H2V20H9A1,1 0 0,1 10,19H11V17H7C5.89,17 5,16.1 5,15V5A2,2 0 0,1 7,3H17M12,14.5L16.5,10H13V6H11V10H7.5L12,14.5Z"
              @click="loadHistoricalData">
              Charger
            </v-btn>
          </div>

          <!-- Coordinate display -->
          <div class="d-flex align-center flex-wrap" style="gap: 8px;">
            <v-icon icon="M12,6.5A2.5,2.5 0 0,1 14.5,9A2.5,2.5 0 0,1 12,11.5A2.5,2.5 0 0,1 9.5,9A2.5,2.5 0 0,1 12,6.5M12,2A7,7 0 0,1 19,9C19,14.25 12,22 12,22C12,22 5,14.25 5,9A7,7 0 0,1 12,2M12,4A5,5 0 0,0 7,9C7,10 7,12 12,18.71C17,12 17,10 17,9A5,5 0 0,0 12,4Z" size="16" color="info" class="me-1"/>
            <v-chip size="small" color="info" variant="tonal" label>
              Lat&nbsp;<strong>{{ latitude }}</strong>
            </v-chip>
            <v-chip size="small" color="info" variant="tonal" label>
              Lng&nbsp;<strong>{{ longitude }}</strong>
            </v-chip>
            <span class="text-caption text-medium-emphasis ms-1">
              Cliquez sur la carte pour changer la position
            </span>
          </div>
        </div>

        <!-- Right: place-picker map -->
        <div style="flex: 1 1 340px; min-width: 260px; position: relative;">
          <div class="map-wrapper">
            <MapboxMap
              ref="mapRef"
              style="width: 100%; height: 100%;"
              :center="mapInitialCenter"
              :access-token="accessToken"
              map-style="mapbox://styles/mapbox/satellite-streets-v12"
              :zoom="10"
              @mb-click="onMapClick">
              <MapboxLayer id="champs-polygons"     :options="champsLayerOptions"/>
              <MapboxLayer id="champs-outlines"     :options="champsOutlineLayerOptions"/>
              <MapboxLayer id="champs-labels"       :options="champsLabelLayerOptions"/>
              <MapboxLayer id="champs-group-labels" :options="champsGroupLabelLayerOptions"/>
              <MapboxMarker :lng-lat="markerPos"/>
            </MapboxMap>
          </div>

          <!-- Producer filter overlay -->
          <div class="map-overlay-tl">
            <v-menu location="bottom start" :close-on-content-click="false">
              <template #activator="{ props: menuProps }">
                <v-btn
                  v-bind="menuProps"
                  size="small"
                  icon
                  color="white"
                  style="background: rgba(0,0,0,0.55); backdrop-filter: blur(6px);"
                  title="Filtre producteurs">
                  <v-icon size="18">M12,18.54L19.37,12.8L21,14.07L12,21.07L3,14.07L4.62,12.81L12,18.54M12,16L3,9L12,2L21,9L12,16M12,4.53L6.26,9L12,13.47L17.74,9L12,4.53Z</v-icon>
                </v-btn>
              </template>
              <v-card rounded="lg" elevation="8" style="min-width: 210px;">
                <div class="d-flex align-center justify-space-between px-3 pt-2 pb-1">
                  <span class="text-caption font-weight-bold text-uppercase" style="letter-spacing: 0.07em;">Producteurs</span>
                  <div class="d-flex" style="gap: 2px;">
                    <v-btn size="x-small" variant="text" @click="selectedProducers = [...producerColorMap.keys()]">Tous</v-btn>
                    <v-btn size="x-small" variant="text" @click="selectedProducers = []">Aucun</v-btn>
                  </div>
                </div>
                <v-divider/>
                <div class="d-flex align-center px-3 py-1" style="gap: 8px;">
                  <v-switch v-model="showChamps" density="compact" hide-details color="primary" style="flex-shrink:0;"/>
                  <span class="text-body-2">Afficher les champs</span>
                </div>
                <v-divider/>
                <div style="overflow-y: auto; max-height: 280px; padding: 4px 8px 8px;">
                  <div
                    v-for="[producer, color] in producerColorMap"
                    :key="producer"
                    class="d-flex align-center"
                    style="gap: 6px; padding: 1px 0;">
                    <v-checkbox
                      v-model="selectedProducers"
                      :value="producer"
                      density="compact"
                      hide-details
                      :color="color"
                      style="flex-shrink: 0;"/>
                    <span class="text-body-2" style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 130px;">{{ producer }}</span>
                    <span :style="{ width: '10px', height: '10px', borderRadius: '50%', background: color, flexShrink: '0', marginLeft: 'auto' }"/>
                  </div>
                </div>
              </v-card>
            </v-menu>
          </div>

          <div class="map-hint">
            <v-icon icon="M11.5,11L17.88,16.37L17,16.55L16.36,16.67C15.73,16.8 15.37,17.5 15.65,18.07L15.92,18.65L17.28,21.59L15.86,22.25L14.5,19.32L14.24,18.74C13.97,18.15 13.22,17.97 12.72,18.38L12.21,18.78L11.5,19.35V11M10.76,8.69A0.76,0.76 0 0,0 10,9.45V20.9C10,21.32 10.34,21.66 10.76,21.66C10.95,21.66 11.11,21.6 11.24,21.5L13.15,19.95L14.81,23.57C14.94,23.84 15.21,24 15.5,24C15.61,24 15.72,24 15.83,23.92L18.59,22.64C18.97,22.46 19.15,22 18.95,21.63L17.28,18L19.69,17.55C19.85,17.5 20,17.43 20.12,17.29C20.39,16.97 20.35,16.5 20,16.21L11.26,8.86L11.25,8.87C11.12,8.76 10.95,8.69 10.76,8.69M15,10V8H20V10H15M13.83,4.76L16.66,1.93L18.07,3.34L15.24,6.17L13.83,4.76M10,0H12V5H10V0M3.93,14.66L6.76,11.83L8.17,13.24L5.34,16.07L3.93,14.66M3.93,3.34L5.34,1.93L8.17,4.76L6.76,6.17L3.93,3.34M7,10H2V8H7V10" size="14" class="me-1"/>
            Cliquer pour choisir un point
          </div>
        </div>

      </div>
    </div>

    <v-divider/>

    <!-- Chart section -->
    <template v-if="items.length > 0 && !isLoading">
    <div class="section-header px-5 pt-4 pb-2">
      <div class="d-flex align-center justify-space-between flex-wrap" style="gap: 8px;">
        <div class="d-flex align-center" style="gap: 10px;">
          <v-avatar color="success" variant="tonal" size="36" rounded="lg">
            <v-icon icon="M16,11.78L20.24,4.45L21.97,5.45L16.74,14.5L10.23,10.75L5.46,19H22V21H2V3H4V17.54L9.5,8L16,11.78Z" size="20"/>
          </v-avatar>
          <div>
            <div class="text-subtitle-1 font-weight-bold" style="line-height: 1.2;">Graphique</div>
            <div class="text-caption text-medium-emphasis">Évolution journalière</div>
          </div>
        </div>
        <v-btn
          color="primary" variant="tonal" rounded="pill" size="small"
          prepend-icon="M5,20H19V18H5M19,9H15V3H9V9H5L12,16L19,9Z"
          @click="exportCSV">
          Exportation CSV
        </v-btn>
      </div>
    </div>
    <div class="px-4 pb-4">
      <v-chart style="min-width: 200px; min-height: 340px;" :option="chartOptions" autoresize/>
    </div>

    <v-divider/>
    </template>

    <!-- Table section -->
    <div class="section-header px-5 pt-4 pb-2">
      <div class="d-flex align-center" style="gap: 10px;">
        <v-avatar color="warning" variant="tonal" size="36" rounded="lg">
          <v-icon icon="M5,4H19A2,2 0 0,1 21,6V18A2,2 0 0,1 19,20H5A2,2 0 0,1 3,18V6A2,2 0 0,1 5,4M5,8V12H11V8H5M13,8V12H19V8H13M5,14V18H11V14H5M13,14V18H19V14H13Z" size="20"/>
        </v-avatar>
        <div>
          <div class="text-subtitle-1 font-weight-bold" style="line-height: 1.2;">Données brutes</div>
          <div class="text-caption text-medium-emphasis">
            {{ items.length }} entrée{{ items.length !== 1 ? 's' : '' }}
          </div>
        </div>
      </div>
    </div>
    <div class="px-4 pb-5">
      <v-data-table
        :headers="headers"
        :items="items"
        density="compact"
        :items-per-page="15"
        class="rounded-lg"
      />
    </div>

  </v-card>
</template>

<style scoped>
.page-header {
  background: v-bind('theme.global.name.value === "dark" ? "rgba(var(--v-theme-surface-variant), 0.25)" : "rgba(var(--v-theme-primary), 0.04)"');
}

.section-header {
  background: v-bind('theme.global.name.value === "dark" ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.015)"');
}

.map-wrapper {
  height: 240px;
  border-radius: 12px;
  overflow: hidden;
  cursor: crosshair;
}

.map-overlay-tl {
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 10;
}

.map-hint {
  position: absolute;
  bottom: 8px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  font-size: 0.72rem;
  padding: 3px 10px;
  border-radius: 20px;
  pointer-events: none;
  white-space: nowrap;
  display: flex;
  align-items: center;
  backdrop-filter: blur(4px);
}
</style>
